import styled from '@emotion/styled';
import { ArrowDirection } from 'components/Elements/HorizontalScroll/HorizontalScroll';
import Typography from 'components/Typography/Typography';
import { pxToEm, theme, palette, hexToRGBA } from 'theme';

const OFFSET = pxToEm(16);
const FEATURED_ITEM_WIDTH = 400;
const REGULAR_ITEM_WIDTH = 328;

export const Header = styled.div<{
  ctaPosition: 'right' | 'left';
  isHomePage: boolean;
}>`
  display: flex;
  padding: ${pxToEm(16)} 0;
  gap: ${OFFSET};

  justify-content: ${({ ctaPosition }) =>
    ctaPosition === 'right' ? 'space-between' : 'flex-start'};

  ${theme.breakpoints.down('md')} {
    padding: 0 0 ${pxToEm(16)} 0;
  }

  ${({ isHomePage }) =>
    isHomePage
      ? `margin-left: auto;
    margin-right: auto;
    max-width: 1376px;
    width: calc(100vw - 2rem)`
      : ''};
`;

export const Block = styled.div`
  margin: ${pxToEm(32)} 0;

  &:first-of-type {
    margin-top: 0;

    ${Header} {
      padding-top: ${pxToEm(16)};
    }
  }
`;

export const Title = styled(Typography)`
  cursor: pointer;

  ${theme.breakpoints.down('md')} {
    font-size: ${pxToEm(18)};
    line-height: ${pxToEm(24)};
  }
`;

export const Link = styled.button<{ alternateDesign?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  outline: 0;
  border: 0;
  margin: 0;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  text-decoration: none;
  color: ${palette.common.white};
  padding: ${pxToEm(6)} ${pxToEm(16)};
  border-radius: 4px;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  background-color: ${palette.primary.main};
  box-shadow: none;
  font-weight: 400;
  text-transform: none;
  white-space: nowrap;
  min-width: ${pxToEm(64)};
  border-radius: 2.5em;
  padding: 0.375em 1em;
  font-size: ${pxToEm(16)};
  line-height: 1.375em;

  ${theme.breakpoints.down('md')} {
    padding: ${pxToEm(4)} ${pxToEm(8)};
  }

  ${({ alternateDesign }) =>
    !alternateDesign &&
    `
        &:hover {
            box-shadow: 0 ${pxToEm(4)} ${pxToEm(25)} rgba(0, 0, 0, 0.08);
        };
    `};

  ${({ alternateDesign }) =>
    alternateDesign &&
    `
        background-color: ${palette.common.white};
        color: ${palette.primary.main};

        &::before{
            content: '';
            position: absolute;
            height: 1rem;
            width: 1px;
            background-color: #E4E4E4;
            display: block;
            left: 0;
        }
    `};
  span {
    padding-top: 0;
    padding-bottom: 0.125rem;
    padding-left: 0.25rem;
    color: #86868b;
  }
`;

export const Wrapper = styled.div`
  position: relative;
  .scrollButtons {
    max-width: 1376px;
    width: calc(100vw - 4rem);
    &.scroll--wide {
      position: relative;
      margin: 0 auto;
      z-index: 1;
      transform: translateY(100px);
    }
  }
`;

export const Viewport = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  overflow: hidden;

  ${theme.breakpoints.down('md')} {
    overflow: visible;
  }
`;

export const Content = styled.ul`
  position: relative;
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  touch-action: pan-y;
  gap: ${OFFSET};
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const ScrollingContent = styled(Content)`
  & li:last-child {
    margin-right: ${OFFSET};
  }
`;

export const HoverContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${hexToRGBA(palette.grey['A700'], 0.8)};
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1;

  ${theme.breakpoints.down('md')} {
    display: none;
  }
`;

export const HoverTitle = styled(Typography)`
  color: ${palette.common.white};
  font-size: 1.5em;
`;

export const HoverSubtitle = styled(Typography)`
  color: ${palette.common.white};
`;

export const HoverCategory = styled(Typography)`
  color: ${palette.common.white};
  padding: ${pxToEm(4)} ${pxToEm(8)};
  background: ${hexToRGBA(palette.common.black, 0.3)};
  border-radius: ${pxToEm(50)};

  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: block;
  }
`;

const getItemSizes = (
  isFeatured?: boolean,
  responsive?: boolean,
  customWidth?: number,
) => {
  if (responsive) {
    return {
      width: '100%',
      height: 'auto',
    };
  }
  const pxWidth =
    customWidth || (isFeatured ? FEATURED_ITEM_WIDTH : REGULAR_ITEM_WIDTH);
  return {
    width: `${pxWidth}px`,
    height: `${Math.floor(pxWidth / 1.67)}px`,
  };
};
export const ItemContainer = styled.li`
  flex: none;
  padding: 0;
  margin: 0;
`;

export const Item = styled.a<{
  isFeatured?: boolean;
  responsive?: boolean;
  customWidth?: number;
}>`
  display: block;
  flex: none;
  position: relative;
  overflow: hidden;
  border-radius: ${pxToEm(20)};
  background: ${palette.secondary?.main};
  width: ${({ isFeatured, responsive, customWidth }) =>
    getItemSizes(isFeatured, responsive, customWidth).width};

  &:after {
    content: '';
    display: block;
    padding-top: 59.88%;
  }

  ${theme.breakpoints.down('md')} {
    margin: 0 ${pxToEm(4)};
    width: 295px;

    ${({ responsive }) =>
      responsive &&
      `
      width: 100%;
      margin: 0;
    `}
  }

  &:hover ${HoverContainer} {
    opacity: 1;
  }

  ${HoverTitle} {
    margin-bottom: ${({ isFeatured }) => (isFeatured ? pxToEm(4) : 0)};
  }

  ${HoverSubtitle} {
    margin-bottom: ${({ isFeatured }) =>
      isFeatured ? pxToEm(24) : pxToEm(16)};
  }

  ${HoverCategory} {
    max-width: ${({ isFeatured }) => (isFeatured ? 'auto' : pxToEm(150))};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
  }
`;

type DirectionProps = { direction: ArrowDirection; show?: boolean };

export const Arrow = styled.div<DirectionProps>`
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  opacity: ${({ show }) => (show ? 1 : 0)};
  height: 100%;
  display: flex;
  align-items: center;
  position: absolute;
  right: ${({ direction }) =>
    direction === ArrowDirection.RIGHT ? pxToEm(-24) : 'auto'};
  left: ${({ direction }) =>
    direction === ArrowDirection.LEFT ? pxToEm(-24) : 'auto'};
  top: 0;
  z-index: 2;
  transition: all 0.1s linear;
  pointer-events: none;

  ${theme.breakpoints.down('md')} {
    display: none;
  }

  & button {
    width: ${pxToEm(48)};
    height: ${pxToEm(48)};
    font-size: 1em;
    background: ${palette.common.white} !important;
    color: ${palette.primary.main} !important;
    pointer-events: auto;
    box-shadow: ${theme.shadows[3]};

    &:focus,
    &:active {
      color: ${palette.primary.main} !important;
      background: ${palette.secondary?.main} !important;
    }
  }
`;

export const HoverContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${pxToEm(16)};
  text-align: center;

  ${theme.breakpoints.down('md')} {
    display: none;
  }
`;

export const HoverCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${pxToEm(8)};
`;

// Legacy styles
export const Info = styled.div<{
  hasLabel?: boolean;
  shouldHideOverflow?: boolean;
}>`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  position: absolute;
  top: ${pxToEm(16)};
  left: ${pxToEm(16)};
  z-index: 2;
`;

export const LegacyTitle = styled(Typography)<{ color?: string }>`
  color: ${({ color }) => color || palette.common.white};
`;

export const LegacyContent = styled(Typography)<{ color?: string }>`
  font-size: ${pxToEm(20)};
  color: ${({ color }) => color || palette.common.white};
`;

export const LegacyDescription = styled(Typography)<{ color?: string }>`
  color: ${({ color }) => color || palette.common.white};

  ${theme.breakpoints.down('md')} {
    display: none;
  }
`;

export const LinkWrapper = styled.div`
  position: relative;
`;

export const ScrollAction = styled.div`
  -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  touch-action: manipulation;
  display: inline-flex;
  text-decoration: none;
  cursor: pointer;
  margin: 0;
  box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-end;
  color: var(--text-body);
  font-weight: 700;
  font-size: 1.4rem;
  padding: 0 2.4rem;
  width: 2rem;
  height: 2rem;
  margin-left: auto; // align to the right

  button {
    background-color: transparent;
    &:hover {
      background-color: transparent;
    }

    svg {
      width: 2rem;
    }
  }
`;
export const SpaceHolder = styled.div`
  min-height: 232px;
`;
